import type { TFunction } from 'i18next'

/**
 * PR-047/UX-038 (Wave U Phase 1): the single place raw engineering strings
 * (`chrome:macos`, `zh-TW`, `Asia/Taipei`, absolute timestamps) turn into
 * human-readable text for user-facing surfaces. Internal API/DB values stay
 * unchanged — this module only formats what already reached the client.
 */

const BROWSER_LABELS: Record<string, string> = {
  chrome: 'Chrome',
  firefox: 'Firefox',
  safari: 'Safari',
  edge: 'Edge',
  opera: 'Opera',
}

const PLATFORM_LABELS: Record<string, string> = {
  macos: 'macOS',
  windows: 'Windows',
  linux: 'Linux',
  ios: 'iOS',
  android: 'Android',
}

/**
 * `"chrome:macos"` -> `"Chrome · macOS"` (BR-094/UX-043). Anything
 * unrecognized (including the `"unknown:unknown"` bucket the backend uses
 * for an unparsed User-Agent) falls back to a generic, still-human label
 * rather than leaking the raw pair.
 */
export function formatClientLabel(clientLabel: string | null, t: TFunction): string {
  if (clientLabel === null) {
    return t('account:security.sessionsSection.unknownDevice')
  }
  const [browser, platform] = clientLabel.split(':')
  const browserLabel = browser !== undefined ? BROWSER_LABELS[browser] : undefined
  const platformLabel = platform !== undefined ? PLATFORM_LABELS[platform] : undefined
  if (browserLabel === undefined && platformLabel === undefined) {
    return t('account:security.sessionsSection.unknownDevice')
  }
  return [browserLabel, platformLabel]
    .filter((part): part is string => part !== undefined)
    .join(' · ')
}

const LOCALE_NAME_KEYS: Record<string, string> = {
  'zh-TW': 'nav:languages.zhTW',
  en: 'nav:languages.en',
}

/** `"zh-TW"` -> `"繁體中文"` / `"English"` (BR-094/UX-038). */
export function formatLocaleName(locale: string, t: TFunction): string {
  const key = LOCALE_NAME_KEYS[locale]
  return key !== undefined ? t(key) : locale
}

/**
 * The fixed set of zones the Competition Basic Information timezone selector
 * offers (SDD §6.6 — "使用 IANA timezone selector", not free text). The
 * server accepts any zone `Intl.supportedValuesOf('timeZone')` recognizes,
 * not just these; this list is a curated, human-labeled subset for the
 * dropdown, matching the zones this app already has city names for below.
 */
export const SELECTABLE_TIMEZONES = [
  'Asia/Taipei',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Singapore',
  'Asia/Seoul',
  'America/Los_Angeles',
  'America/New_York',
  'Europe/London',
  'UTC',
] as const

const TIMEZONE_CITY_NAMES: Record<string, { zhTW: string; en: string }> = {
  'Asia/Taipei': { zhTW: '台北', en: 'Taipei' },
  'Asia/Tokyo': { zhTW: '東京', en: 'Tokyo' },
  'Asia/Shanghai': { zhTW: '上海', en: 'Shanghai' },
  'Asia/Hong_Kong': { zhTW: '香港', en: 'Hong Kong' },
  'Asia/Singapore': { zhTW: '新加坡', en: 'Singapore' },
  'Asia/Seoul': { zhTW: '首爾', en: 'Seoul' },
  'America/Los_Angeles': { zhTW: '洛杉磯', en: 'Los Angeles' },
  'America/New_York': { zhTW: '紐約', en: 'New York' },
  'Europe/London': { zhTW: '倫敦', en: 'London' },
  UTC: { zhTW: 'UTC', en: 'UTC' },
}

/** Best-effort fallback city label for an IANA zone this app has no explicit name for. */
function fallbackCityLabel(timezone: string): string {
  const lastSegment = timezone.split('/').pop() ?? timezone
  return lastSegment.replace(/_/g, ' ')
}

function utcOffsetLabel(timezone: string): string | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date())
    const offsetPart = parts.find((part) => part.type === 'timeZoneName')?.value
    if (offsetPart === undefined) {
      return null
    }
    // Intl gives "GMT+8"; normalize to "UTC+08:00".
    const match = /^GMT([+-])(\d{1,2})(?::?(\d{2}))?$/.exec(offsetPart)
    if (match === null) {
      return offsetPart === 'GMT' ? 'UTC+00:00' : null
    }
    const [, sign, hours, minutes = '00'] = match
    return `UTC${sign}${(hours ?? '0').padStart(2, '0')}:${minutes}`
  } catch {
    return null
  }
}

/** `"Asia/Taipei"` -> `"台北（UTC+08:00）"` (BR-094/UX-038). Never returns the raw IANA id alone. */
export function formatTimezone(timezone: string, locale: string): string {
  const cityNames = TIMEZONE_CITY_NAMES[timezone]
  const city =
    cityNames !== undefined
      ? locale === 'en'
        ? cityNames.en
        : cityNames.zhTW
      : fallbackCityLabel(timezone)
  const offset = utcOffsetLabel(timezone)
  if (offset === null) {
    return city
  }
  return locale === 'en' ? `${city} (${offset})` : `${city}（${offset}）`
}

const MINUTE_MS = 60 * 1000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

function isSameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function pad2(value: number): string {
  return value.toString().padStart(2, '0')
}

function timeOfDay(date: Date): string {
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`
}

/**
 * BR-094/UX-038/UX-043: the *primary* display for a timestamp like a
 * session's last-active time must be relative, not an absolute
 * `2026/8/21 上午6:25:07`-style string. A full absolute timestamp may still
 * appear in a detail view or tooltip via the `title` attribute — this
 * function is only responsible for the primary text.
 */
export function formatRelativeTime(iso: string, t: TFunction, now: Date = new Date()): string {
  const then = new Date(iso)
  const diffMs = now.getTime() - then.getTime()

  if (diffMs < MINUTE_MS) {
    return t('common:relativeTime.justNow')
  }
  if (diffMs < HOUR_MS) {
    return t('common:relativeTime.minutesAgo', { count: Math.floor(diffMs / MINUTE_MS) })
  }
  if (isSameCalendarDay(then, now)) {
    return t('common:relativeTime.todayAt', { time: timeOfDay(then) })
  }
  const yesterday = new Date(now.getTime() - DAY_MS)
  if (isSameCalendarDay(then, yesterday)) {
    return t('common:relativeTime.yesterdayAt', { time: timeOfDay(then) })
  }
  return t('common:relativeTime.dateAt', {
    month: then.getMonth() + 1,
    day: then.getDate(),
    time: timeOfDay(then),
  })
}

/**
 * UX-274 (Student Participant UI/UX/IA v4.45, Wave E): the Participant
 * Overview's "下一個里程碑" card needs a relative day count ("還有 47 天")
 * as a supplement to (never a replacement for) the absolute deadline text —
 * `formatRelativeTime` above is deliberately the wrong tool here: it always
 * describes the past ("5 分鐘前"/"昨天"), while a milestone card describes a
 * future date. Calendar-day difference (not a raw 24h-bucket division) so
 * "23:59 tonight" reads as "還有 0 天" (today), not "還有 1 天" from a
 * same-day-but-earlier `now`. Negative once the date has passed — callers
 * decide whether/how to show that (this delivery's Next Milestone card only
 * ever calls it with a still-future date, since `getCompetitionNextMilestone`
 * only returns dates for the competition's *current* phase).
 */
export function daysUntil(iso: string, now: Date = new Date()): number {
  const target = new Date(iso)
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const startOfTarget = new Date(target.getFullYear(), target.getMonth(), target.getDate())
  return Math.round((startOfTarget.getTime() - startOfToday.getTime()) / DAY_MS)
}

/**
 * ISSUE-PREVIEW-20260820-005: the student competition page rendered a
 * deadline as the raw stored ISO string (`2026-11-13T15:59:00.000Z`)
 * instead of a locale-formatted date — the Public Page's "Important Dates"
 * (`schedule-timeline.tsx`) already got this right for the same kind of
 * far-future deadline; this is that same `toLocaleString()` approach,
 * reused so every "when does this close" display in the app agrees. A
 * relative "in 84 days" style (see `formatRelativeTime` above) is
 * deliberately not used here — a deadline needs to stay precise and
 * checkable at a glance, not decay into vague relative text.
 */
export function formatDeadline(iso: string | null): string | null {
  if (iso === null) return null
  return new Date(iso).toLocaleString()
}

/**
 * P0 student-workspace UX polish: `formatDeadline` above delegates to
 * `toLocaleString()`, which renders differently per browser locale (e.g.
 * `2026/8/21 上午6:25:07` in zh-TW vs `8/21/2026, 6:25:07 AM` in en) — the
 * user asked for one identical pattern (`YYYY/MM/DD hh:mm:ss A`) regardless
 * of the app's language, always anchored to the competition platform's
 * operating timezone (Asia/Taipei) rather than the visitor's browser zone,
 * since a deadline must read the same way for every student comparing
 * notes. `withTimezone` appends the fixed `(UTC+08:00)` suffix — Taipei has
 * no DST, so this is safe to hardcode rather than recomputing per call.
 */
export function formatDateTimeTaipei(
  iso: string | null,
  options: { withTimezone?: boolean; seconds?: boolean } = {},
): string | null {
  if (iso === null) return null
  const { withTimezone = false, seconds = true } = options
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    ...(seconds ? { second: '2-digit' } : {}),
    hour12: true,
  }).formatToParts(new Date(iso))
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? ''
  const formatted = seconds
    ? `${get('year')}/${get('month')}/${get('day')} ${get('hour')}:${get('minute')}:${get('second')} ${get('dayPeriod')}`
    : `${get('year')}/${get('month')}/${get('day')} ${get('hour')}:${get('minute')} ${get('dayPeriod')}`
  return withTimezone ? `${formatted} (UTC+08:00)` : formatted
}
